// Presentation-only enum → plain-Dutch label mapping.
// DB values (PROPOSED, MODELLED_ESTIMATE, TIER_A, ...) never change.
import t from "../i18n";

const MAPS = {
  outcome: "enum.outcome",
  status: "enum.status",
  evidence: "enum.evidence",
  tier: "enum.tier",
  knowledge_type: "enum.knowledge_type",
  reliability: "enum.reliability",
  action_type: "enum.action_type",
  source: "enum.source",
  sector: "enum.sector",
  permission: "enum.permission",
  confidence_pill: "enum.confidence_pill",
  comparison_verdict: "enum.comparison_verdict",
  comparison_source: "enum.comparison_source",
  intake_field: "intake_field",
};

export function formatEnum(kind, key, fallback) {
  if (key == null) return fallback ?? "—";
  const ns = MAPS[kind];
  if (!ns) return fallback ?? String(key);
  const nl = t(`${ns}.${key}`);
  if (nl && !nl.startsWith("enum.")) return nl;
  // Try uppercased for safety
  const nl2 = t(`${ns}.${String(key).toUpperCase()}`);
  if (nl2 && !nl2.startsWith("enum.")) return nl2;
  return fallback ?? String(key);
}

// NL-NL currency / percent formatters — use for any user-facing number.
const _EUR = new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const _EUR2 = new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });
const _PCT1 = new Intl.NumberFormat("nl-NL", { style: "percent", maximumFractionDigits: 1 });

export function fmtEUR(v, decimals = 0) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return (decimals > 0 ? _EUR2 : _EUR).format(Number(v));
}
export function fmtEURRange(lo, hi) {
  return `${fmtEUR(lo)} – ${fmtEUR(hi)}`;
}
export function fmtPct(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  return _PCT1.format(n > 1 ? n / 100 : n);
}

// -----------------------------------------------------------------------------
// Metric-change formatter — builds a natural NL sentence from a
// (direction, metric_key, low_pct, high_pct) tuple. Used by DecisionCard and
// friends to replace the previous "DOWN spend_eur (5–30%)" raw rendering.
// -----------------------------------------------------------------------------
const _DIRECTION_VERB = {
  UP:     { noun: "stijging", verb: "stijging" },
  DOWN:   { noun: "daling",   verb: "daling"   },
  STABLE: { noun: "stabilisatie", verb: "stabilisatie" },
};
const _METRIC_NL = {
  spend_eur:   { of: "de advertentiekosten" },
  spend:       { of: "de advertentiekosten" },
  conversions: { of: "de conversies" },
  clicks:      { of: "de klikken" },
  impressions: { of: "de vertoningen" },
  cpa:         { of: "de kosten per aanvraag" },
  roas:        { of: "de opbrengst per euro reclamekosten" },
  ctr:         { of: "de doorklikratio" },
  cvr:         { of: "de conversieratio" },
};

function _fmtPctPlain(v) {
  if (v == null || Number.isNaN(Number(v))) return null;
  const s = Number(v).toLocaleString("nl-NL", { maximumFractionDigits: 1 });
  return `${s}%`;
}

export function formatMetricChange(direction, metric, lowPct, highPct) {
  const dir = _DIRECTION_VERB[direction] || null;
  const met = _METRIC_NL[metric] || { of: metric || "een indicator" };
  const lo = _fmtPctPlain(lowPct);
  const hi = _fmtPctPlain(highPct);
  const range = (lo && hi) ? `${lo}–${hi}` : (lo || hi || "onbekend");
  if (!dir) {
    // Fallback: generic "verandering"
    return `Verwachte verandering van ${met.of}: ${range}.`;
  }
  const cap = dir.noun.charAt(0).toUpperCase() + dir.noun.slice(1);
  return `Verwachte ${dir.noun} van ${met.of}: ${range}.`
    .replace(/^Verwachte/, `Verwachte`) // keep casing
    .replace(/verwachte verwachte/i, "verwachte")
    || `Verwachte ${cap.toLowerCase()} van ${met.of}: ${range}.`;
}

export default formatEnum;
